// src/components/Navbar.js

import React, { useState } from 'react'
import {
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button as MuiButton,
  Typography
} from '@mui/material'

import HelpIcon from '@mui/icons-material/Help';

import { PlayArrow, ArrowBack, Close } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

import {
  Menu as MenuIcon,
  AdminPanelSettings as AdminIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Person as ProfileIcon,
  RateReview as RateReviewIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

// Çoklu dil desteği
import { useTranslation } from 'react-i18next'

export default function Navbar() {
  const navigate = useNavigate()
  const { currentUser, userRole, logout } = useAuth()
  const { t, i18n } = useTranslation()
  const theme = useTheme();

  // Drawer (hamburger) menü kontrolü
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Add state for help modal and selected video
  const [helpModalOpen, setHelpModalOpen] = useState(false)
  const [selectedVideo, setSelectedVideo] = useState(null)

  // Video URLs (replace with actual URLs)
  const videoUrls = [
    'https://codini.evoosurvey.com/videos/0.mp4',
    'https://codini.evoosurvey.com/videos/1.mp4',
    'https://codini.evoosurvey.com/videos/2.mp4',
    'https://codini.evoosurvey.com/videos/3.mp4',
    'https://codini.evoosurvey.com/videos/4.mp4',
    'https://codini.evoosurvey.com/videos/5.mp4',
    'https://codini.evoosurvey.com/videos/6.mp4',
    'https://codini.evoosurvey.com/videos/7.mp4',
  ]

  // Profil menüsü için anchor
  const [anchorEl, setAnchorEl] = useState(null)
  const openProfile = Boolean(anchorEl)

  // Raporlar menüsü için anchor
  const [raporlarAnchorEl, setRaporlarAnchorEl] = useState(null)
  const openRaporlar = Boolean(raporlarAnchorEl)

  // Ayarlar menüsü için anchor
  const [ayarlarAnchorEl, setAyarlarAnchorEl] = useState(null)
  const openAyarlar = Boolean(ayarlarAnchorEl)

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen)
  }

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleProfileMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    try {
      navigate('/login')
      await logout()
    } catch (error) {
      console.error('Çıkış yapılırken hata:', error)
    }
    handleProfileMenuClose()
  }

  // Fare ile "Raporlar" menüsünün üzerine gelince aç
  const handleRaporlarMouseEnter = (event) => {
    setRaporlarAnchorEl(event.currentTarget)
  }
  // Fare "Raporlar"dan ayrılınca kapat
  const handleRaporlarMouseLeave = () => {
    setRaporlarAnchorEl(null)
  }

  // Fare ile "Ayarlar" menüsünün üzerine gelince aç
  const handleAyarlarMouseEnter = (event) => {
    setAyarlarAnchorEl(event.currentTarget)
  }
  // Fare "Ayarlar"dan ayrılınca kapat
  const handleAyarlarMouseLeave = () => {
    setAyarlarAnchorEl(null)
  }

  // Dil değiştirme fonksiyonu
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng)
  }

  // Ana menüdeki öğeler
  const menuItems = [
  
    // Ayarlar (alt menü)
    {
      text: t('navbar.home'),
      icon: <RateReviewIcon fontSize="small" />,
      onClick: () => navigate('/ProjectsPage'),
      visible: userRole === 'user' || userRole === 'admin',
    },
    {
      text: t('navbar.userManagement'),
      icon: <VisibilityIcon fontSize="small" />,
      onClick: () => navigate('/admin-users'),
      visible: userRole === 'admin',
    }

  ]

  return (
    <>
      <AppBar
        position="static"
        sx={{
          backgroundColor: '#5bc0be', // istediğiniz renk kodu
        }}
      >
        <Toolbar
          variant="dense"
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          {/* Mobil Hamburger butonu */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Orta kısım: Logo */}
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              alt="Codini"
              src="https://codini.evoosurvey.com/codini20/logo.png"
              sx={{
                width: 120,
                height: 'auto',
                maxWidth: '50%',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/ProjectsPage')}
            />
          </Box>

          {/* Sağda: Büyük ekranda menü */}
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            {menuItems.map((item, index) =>
              item.visible ? (
                item.subMenu ? (
                  // Eğer subMenu varsa (Raporlar veya Ayarlar), hover ile açılacak
                  <Box
                    key={index}
                    onMouseEnter={
                      // Buradaki kontrol, `item.text`in çeviri olduğu için,
                      // metni değil orijinalde hangi menü olduğuna bakabiliriz.
                      item.text === t('navbar.reports')
                        ? handleRaporlarMouseEnter
                        : handleAyarlarMouseEnter
                    }
                    onMouseLeave={
                      item.text === t('navbar.reports')
                        ? handleRaporlarMouseLeave
                        : handleAyarlarMouseLeave
                    }
                  >
                    <Button
                      color="inherit"
                      startIcon={item.icon}
                      sx={{
                        ml: 1,
                        fontSize: '0.75rem',
                        minWidth: 'auto',
                        padding: '4px 8px',
                      }}
                    >
                      {item.text}
                    </Button>

                    {/* Alt menü (Raporlar veya Ayarlar) */}
                    <Menu
                      anchorEl={
                        item.text === t('navbar.reports')
                          ? raporlarAnchorEl
                          : ayarlarAnchorEl
                      }
                      open={
                        item.text === t('navbar.reports')
                          ? openRaporlar
                          : openAyarlar
                      }
                      onClose={
                        item.text === t('navbar.reports')
                          ? handleRaporlarMouseLeave
                          : handleAyarlarMouseLeave
                      }
                      MenuListProps={{
                        onMouseEnter: () =>
                          item.text === t('navbar.reports')
                            ? setRaporlarAnchorEl(raporlarAnchorEl)
                            : setAyarlarAnchorEl(ayarlarAnchorEl),
                        onMouseLeave:
                          item.text === t('navbar.reports')
                            ? handleRaporlarMouseLeave
                            : handleAyarlarMouseLeave,
                        style: { pointerEvents: 'auto' }, // Hover çalışması için
                      }}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                    >
                      {item.subMenu.map(
                        (sub, subIndex) =>
                          sub.visible && (
                            <MenuItem
                              key={subIndex}
                              onClick={() => {
                                sub.onClick()
                                // Alt menü kapat
                                item.text === t('navbar.reports')
                                  ? handleRaporlarMouseLeave()
                                  : handleAyarlarMouseLeave()
                              }}
                              sx={{ fontSize: '0.75rem' }}
                            >
                              <ListItemIcon>{sub.icon}</ListItemIcon>
                              {sub.text}
                            </MenuItem>
                          )
                      )}
                    </Menu>
                  </Box>
                ) : (
                  // Normal menü öğesi (subMenu yoksa)
                  <Button
                    key={index}
                    color="inherit"
                    sx={{
                      ml: 1,
                      fontSize: '0.75rem',
                      minWidth: 'auto',
                      padding: '4px 8px',
                    }}
                    startIcon={item.icon}
                    onClick={item.onClick}
                  >
                    {item.text}
                  </Button>
                )
              ) : null
            )}
          </Box>

          {/* Dil Değiştirme Butonları (Büyük Ekranda) */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, ml: 2 }}>
            <Button
              color="inherit"
              onClick={() => changeLanguage('en')}
              sx={{ fontSize: '0.75rem', minWidth: 'auto', padding: '4px 8px' }}
            >
              EN
            </Button>
            <Button
              color="inherit"
              onClick={() => changeLanguage('tr')}
              sx={{ fontSize: '0.75rem', minWidth: 'auto', padding: '4px 8px' }}
            >
              TR
            </Button>
            <Button
              color="inherit"
              onClick={() => changeLanguage('de')}
              sx={{ fontSize: '0.75rem', minWidth: 'auto', padding: '4px 8px' }}
            >
              DE
            </Button>
            <Button
              color="inherit"
              onClick={() => changeLanguage('fr')}
              sx={{ fontSize: '0.75rem', minWidth: 'auto', padding: '4px 8px' }}
            >
              FR
            </Button>
            <Button
              color="inherit"
              onClick={() => changeLanguage('es')}
              sx={{ fontSize: '0.75rem', minWidth: 'auto', padding: '4px 8px' }}
            >
              ES
            </Button>
            <Button
              color="inherit"
              onClick={() => changeLanguage('it')}
              sx={{ fontSize: '0.75rem', minWidth: 'auto', padding: '4px 8px' }}
            >
              IT
            </Button>
          </Box>

          {/* Sağda: Profil Avatar veya Giriş butonu */}
          <Box sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
            {currentUser && (
              <Tooltip title={t('navbar.help')}>
                <Button
                  color="inherit"
                  startIcon={<HelpIcon />}
                  onClick={() => setHelpModalOpen(true)}
                  sx={{
                    [theme.breakpoints.down('sm')]: {
                      padding: '6px',
                      minWidth: 'auto',
                    },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    {t('navbar.help')}
                  </Box>
                </Button>
              </Tooltip>
            )}
            {currentUser ? (
              <>
                <Tooltip title={t('navbar.profile')}>
                  <IconButton onClick={handleProfileMenuOpen}>
                    <Avatar
                      alt={currentUser.nickname || currentUser.email}
                      src={currentUser.avatarUrl || ''}
                      sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}
                    >
                      {currentUser.nickname
                        ? currentUser.nickname.charAt(0)
                        : currentUser.email.charAt(0)}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={anchorEl}
                  open={openProfile}
                  onClose={handleProfileMenuClose}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      navigate('/profile')
                      handleProfileMenuClose()
                    }}
                  >
                    <ListItemIcon>
                      <ProfileIcon fontSize="small" />
                    </ListItemIcon>
                    {t('navbar.profile')}
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    {t('navbar.logout')}
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button
                color="inherit"
                startIcon={<LoginIcon fontSize="small" />}
                onClick={() => navigate('/login')}
                sx={{
                  fontSize: '0.75rem',
                  padding: '4px 8px',
                }}
              >
                {t('navbar.login')}
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobil Drawer (Hamburger Menü) */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 220 },
        }}
      >
        <Box sx={{ width: 220 }} role="presentation">
          <List>
            {menuItems.map((item, index) =>
              item.visible ? (
                item.subMenu ? (
                  // Raporlar veya Ayarlar gibi alt menü varsa, burada alt öğeleri liste halinde gösteriyoruz
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{ fontSize: '0.75rem' }}
                      />
                    </ListItem>
                    {/* Alt öğeleri sıralıyoruz */}
                    {item.subMenu.map(
                      (sub, subIndex) =>
                        sub.visible && (
                          <ListItem
                            button
                            key={subIndex}
                            sx={{ pl: 4 }} // Biraz iç boşluk
                            onClick={() => {
                              sub.onClick()
                              handleDrawerToggle()
                            }}
                          >
                            <ListItemIcon>{sub.icon}</ListItemIcon>
                            <ListItemText
                              primary={sub.text}
                              primaryTypographyProps={{ fontSize: '0.75rem' }}
                            />
                          </ListItem>
                        )
                    )}
                  </React.Fragment>
                ) : (
                  <ListItem
                    button
                    key={index}
                    onClick={() => {
                      item.onClick()
                      handleDrawerToggle()
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{ fontSize: '0.75rem' }}
                    />
                  </ListItem>
                )
              ) : null
            )}
          </List>

          <Divider />

          {/* Dil Değiştirme Butonları (Mobil) */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', mt: 1 }}>
          <Button size="small" onClick={() => changeLanguage('en')} sx={{ mr: 1 }}>
              EN
            </Button>
            <Button size="small" onClick={() => changeLanguage('tr')} sx={{ mr: 1 }}>
              TR
            </Button>
            <Button size="small" onClick={() => changeLanguage('de')} sx={{ mr: 1 }}>
              DE
            </Button>
            <Button size="small" onClick={() => changeLanguage('fr')} sx={{ mr: 1 }}>
              FR
            </Button>
            <Button size="small" onClick={() => changeLanguage('es')} sx={{ mr: 1 }}>
              ES
            </Button>
            <Button size="small" onClick={() => changeLanguage('it')} sx={{ mr: 1 }}>
              IT
            </Button>
          </Box>
          <Divider sx={{ mt: 1 }} />

          {currentUser ? (
            <List>
              <ListItem
                button
                onClick={() => {
                  navigate('/profile')
                  handleDrawerToggle()
                }}
              >
                <ListItemIcon>
                  <ProfileIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t('navbar.profile')}
                  primaryTypographyProps={{ fontSize: '0.75rem' }}
                />
              </ListItem>
              <ListItem
                button
                onClick={() => {
                  handleLogout()
                  handleDrawerToggle()
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t('navbar.logout')}
                  primaryTypographyProps={{ fontSize: '0.75rem' }}
                />
              </ListItem>
            </List>
          ) : (
            <List>
              <ListItem
                button
                onClick={() => {
                  navigate('/login')
                  handleDrawerToggle()
                }}
              >
                <ListItemIcon>
                  <LoginIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t('navbar.login')}
                  primaryTypographyProps={{ fontSize: '0.75rem' }}
                />
              </ListItem>
            </List>
          )}
        </Box>
      </Drawer>

       {/* Help Modal */}
        <Dialog
          open={helpModalOpen}
          onClose={() => {
            setHelpModalOpen(false);
            setSelectedVideo(null);
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            },
          }}
        >
          <DialogTitle>
            <Box display="flex" alignItems="center">
              {selectedVideo ? (
                <>
                  <PlayArrow sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">{t('help.watchingVideo')}</Typography>
                </>
              ) : (
                <Typography variant="h6">{t('help.title')}</Typography>
              )}
            </Box>
            <IconButton
              aria-label="kapat"
              onClick={() => setHelpModalOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {selectedVideo ? (
              <Box sx={{ position: 'relative', paddingTop: '56.25%' /* 16:9 oran */ }}>
                <video
                  controls
                  autoPlay
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    borderRadius: '8px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  }}
                >
                  <source src={videoUrls[selectedVideo - 1]} type="video/mp4" />
                </video>
              </Box>
            ) : (
              <Grid container spacing={2} sx={{ py: 2 }}>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((videoNum) => (
                  <Grid item xs={12} sm={6} md={4} key={videoNum}>
                    <MuiButton
                      fullWidth
                      variant="outlined"
                      onClick={() => setSelectedVideo(videoNum)}
                      startIcon={<PlayArrow />}
                      sx={{
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        borderRadius: '8px',
                        '&:hover': {
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        },
                      }}
                    >
                      {t(`help.video${videoNum}`)}
                    </MuiButton>
                  </Grid>
                ))}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            {selectedVideo && (
              <MuiButton
                onClick={() => setSelectedVideo(null)}
                startIcon={<ArrowBack />}
                sx={{ textTransform: 'none' }}
              >
                {t('help.back')}
              </MuiButton>
            )}
            <MuiButton
              onClick={() => setHelpModalOpen(false)}
              color="primary"
              variant="contained"
              sx={{ borderRadius: '8px' }}
            >
              {t('help.close')}
            </MuiButton>
          </DialogActions>
        </Dialog>
    </>
  )
}